import { m8API } from 'services/production/m8';
import { TGetClientDetailsParams } from 'types/services';
import { TClientDetails, TClientDetailsPaymentsInfo } from 'types/state';
import { normalizeM8UserFullInfo } from 'services/production/utils/normalizeM8UserFullInfo';
import { HTTPResponseError } from 'utils/errors';
import { M8BillRow } from 'services/production/types';
import { normalizeM8PartnerDTO } from 'services/production/utils/normalizeM8PartnerDTO';
import { normalizeM8ProductDTO } from 'services/production/utils/normalizeM8ProductDTO';
import { normalizeM8DemoStackDTO } from 'services/production/utils/normalizeM8DemoStackDTO';
import { normalizeM8PaymentTransactionDTO } from 'services/production/utils/normalizeM8PaymentTransactionDTO';
import { normalizeM8ClientBalance } from '../utils/normalizeM8ClientBalance';

export const getClientDetails = async (
  params: TGetClientDetailsParams
): Promise<TClientDetails> => {
  const { clientId } = params;

  const [
    user,
    m8ProdStackDTO,
    m8DemoStackDTO,
    userTransactions,
    balance,
    legalRequesites,
    M8BillRowsByMonth,
    userStackActivity,
    m8ProductDTOs,
    m8ClientBalance,
  ] = await Promise.all([
    m8API.admin.getUser(clientId),
    m8API.admin.getUserProdStack({ idUser: clientId }),
    m8API.admin.getUserDemoStack({ idUser: clientId }),
    m8API.admin.getPaymentTransactionsByUser(clientId),
    m8API.admin.getAccountBalance({ idUser: clientId }),
    m8API.admin.getUserLegalRequisites({ idUser: clientId }),
    m8API.admin.getBillRows({
      idUser: clientId,
      year: new Date().getFullYear(),
    }),
    m8API.admin.getUserStackActivity({ idUser: clientId }).catch((error) => {
      if (error instanceof HTTPResponseError) {
        if (error.status === 500) {
          return -1;
        }
      }

      throw error;
    }),
    m8API.admin.getProducts({ idUser: clientId }),
    m8API.admin.getClientBalance({ idClient: clientId }),
  ]);

  const partner = user.partner
    ? normalizeM8PartnerDTO(user.partner).toPartner()
    : null;

  const paymentsInfo: TClientDetailsPaymentsInfo = {
    balance,
    transactions: userTransactions.map((transaction) =>
      normalizeM8PaymentTransactionDTO(transaction).toTransactionPayment()
    ),
  };

  const m8BillRows = Object.entries(M8BillRowsByMonth).reduce<M8BillRow[]>(
    (acc, [month, rows]) => {
      return [...acc, ...rows];
    },
    []
  );

  const stackInfo = {
    demoStackInfo: m8DemoStackDTO
      ? normalizeM8DemoStackDTO(m8DemoStackDTO).toDemoStackInfo()
      : null,
    prodStackInfo:
      user.prodUrl !== null
        ? normalizeM8UserFullInfo(user).toProdStackInfo(m8ProdStackDTO)
        : null,
    managersCount: userStackActivity,
  };

  const clientDetails: TClientDetails = {
    clientInfo: normalizeM8UserFullInfo(user).toClientDetailsClientInfo(),
    stackInfo,
    userProfileInfo: normalizeM8UserFullInfo(user).toUserProfileInfo(),
    paymentsInfo,
    legalRequesites,
    m8BillRows,
    partner,
    clientProducts: m8ProductDTOs.map((m8ProductDTO) =>
      normalizeM8ProductDTO(m8ProductDTO).toClientProduct()
    ),
    finances: normalizeM8ClientBalance(m8ClientBalance).toClientFinances(),
  };

  return clientDetails;
};
